import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";
import { ChartTooltipV2, ChartTooltipV2Wrapper } from "@/components/ui/chartV2";
import { ChartDataPoint, ReferenceLineInfo, TickCallback } from "@/types/chart";
import { useChartDimensionsV2 } from "@/hooks/useChartDimensions";
import LegendWrapperV2 from "./components/legendWrapperV2";
import { formatValueV2 } from "@/common/valueFormatter";
import { useCurrentWorkspace } from "@/store/authContext/authContext";
import EventBus from "@/lib/eventBus";
import type { HandleLegendItemChangeArguments } from "@/types/report";
import { SeriesConfig } from "@/common/type/report-types";
import {
  ReportChartSettingsV2,
  ReportExtras,
} from "@/common/report-configuration";
import { resolveCategorySeriesColor } from "@/common/colorUtils";
import ChartAnnotations from "./components/chartAnnotations";

interface HorizontalBarChartComponentV2Props {
  data: ChartDataPoint[];
  xAxis: string;
  xAxisLabel: string; // label for the x axis (shown vertically on left)
  yAxisLeftLabel: string; // label for the y axis (shown horizontally on bottom)
  seriesConfig: SeriesConfig;
  chartSettings: ReportChartSettingsV2;
  height?: number;
  maxHeight?: number;
  handleLegendItemChange: (args: HandleLegendItemChangeArguments) => void;
  stacked?: boolean;
  referenceLines?: ReferenceLineInfo[];
  reportExtras: ReportExtras;
}

const HorizontalBarChartComponentV2: React.FC<
  HorizontalBarChartComponentV2Props
> = ({
  data,
  xAxis,
  xAxisLabel,
  yAxisLeftLabel,
  seriesConfig,
  chartSettings,
  handleLegendItemChange,
  stacked = false,
  maxHeight,
  referenceLines = [],
  reportExtras,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(800);
  const currentWorkspace = useCurrentWorkspace();

  // Measure container width on mount and resize
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        if (width > 0) {
          setContainerWidth(width);
        }
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const dimensions = useChartDimensionsV2(
    chartSettings,
    maxHeight,
    containerWidth,
  );

  // Increase chart height for horizontal bar charts with many Y-axis items
  const adjustedChartHeight = useMemo(() => {
    const BASE_THRESHOLD = 12;
    const EXTRA_HEIGHT_PER_ITEM = 30;
    if (data.length > BASE_THRESHOLD) {
      const extraItems = data.length - BASE_THRESHOLD;
      return dimensions.chartHeight + extraItems * EXTRA_HEIGHT_PER_ITEM;
    }
    return dimensions.chartHeight;
  }, [data.length, dimensions.chartHeight]);

  const CustomYAxisTick = useCallback(({ x, y, payload }: TickCallback) => {
    const CHARS_PER_LINE = 45;
    const MAX_LINES = 2;
    const LINE_HEIGHT = 12;
    const label = payload.value.toString();

    // Split label into lines at word boundaries
    const wrapText = (text: string): string[] => {
      const words = text.split(" ");
      const lines: string[] = [];
      let currentLine = "";

      for (const word of words) {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        if (testLine.length <= CHARS_PER_LINE) {
          currentLine = testLine;
        } else {
          if (currentLine) lines.push(currentLine);
          currentLine = word;
        }
      }
      if (currentLine) lines.push(currentLine);

      // Limit to MAX_LINES and truncate last line if needed
      if (lines.length > MAX_LINES) {
        lines.length = MAX_LINES;
        lines[MAX_LINES - 1] =
          lines[MAX_LINES - 1].substring(0, CHARS_PER_LINE - 3) + "...";
      }
      return lines;
    };

    const lines = wrapText(label);
    const totalHeight = lines.length * LINE_HEIGHT;
    const startY = -totalHeight / 2 + LINE_HEIGHT / 2;

    const clicker = () => {
      EventBus.$emit("report:yAxisLabelClick", label);
    };

    return (
      <g
        transform={`translate(${x},${y})`}
        onClick={clicker}
        style={{ cursor: "pointer" }}
      >
        <text
          x={0}
          y={0}
          textAnchor="end"
          style={{ cursor: "pointer", fontSize: 10 }}
        >
          {lines.length < label.split(" ").length && <title>{label}</title>}
          {lines.map((line, i) => (
            <tspan key={i} x={0} dy={i === 0 ? startY : LINE_HEIGHT}>
              {line}
            </tspan>
          ))}
        </text>
      </g>
    );
  }, []);

  const renderXAxis = useCallback(() => {
    const measure = Array.from(seriesConfig.values())[0].measure;
    return (
      <XAxis
        type="number"
        tickFormatter={(value: number) =>
          formatValueV2(value, measure, currentWorkspace)
        }
        label={{
          value: yAxisLeftLabel,
          position: "bottom",
          angle: 0,
          dx: 0,
          fontSize: 14,
          fontWeight: "bold",
        }}
      />
    );
  }, [yAxisLeftLabel, seriesConfig, currentWorkspace]);

  const renderYAxis = useCallback(() => {
    return (
      <YAxis
        dataKey={xAxis}
        type="category"
        tick={CustomYAxisTick}
        width={230}
        label={{
          value: xAxisLabel,
          position: "center",
          angle: 270,
          dx: -140,
          fontSize: 14,
          fontWeight: "bold",
        }}
      />
    );
  }, [xAxis, xAxisLabel, CustomYAxisTick]);

  const bars = useMemo(() => {
    const visibleSeriesCount = Array.from(seriesConfig.values()).filter(
      (item) => !item.hidden,
    ).length;

    return Array.from(seriesConfig)
      .filter(([, item]) => !item.hidden)
      .map(([key, item]) => {
        return (
          <Bar
            key={key}
            dataKey={key}
            fill={item.color}
            fillOpacity={item.isPreviousPeriod ? 0.55 : 1}
            name={item.label}
            stackId={stacked ? "stack" : undefined}
          >
            {data.map((_, index) => {
              const categoryValue = data[index]?.[xAxis];
              const semanticCellColor =
                visibleSeriesCount === 1 && typeof categoryValue === "string"
                  ? resolveCategorySeriesColor(categoryValue)
                  : undefined;

              return (
                <Cell
                  key={`cell-${key}-${index}`}
                  fill={semanticCellColor ?? item.color}
                />
              );
            })}
          </Bar>
        );
      });
  }, [seriesConfig, stacked, data, xAxis]);
  return (
    <div
      ref={containerRef}
      style={{
        display: "flex",
        flexDirection: ["top", "bottom"].includes(
          chartSettings.legendPosition || "right",
        )
          ? "column"
          : "row",
        alignItems: "center",
        width: "100%",
        height: "100%",
      }}
    >
      {chartSettings.includeLegend &&
        ["left", "top"].includes(chartSettings.legendPosition || "right") && (
          <LegendWrapperV2
            position={chartSettings.legendPosition || "right"}
            items={seriesConfig}
            onItemChange={handleLegendItemChange}
          />
        )}

      <div
        style={{
          flex: 1,
          minHeight: 0,
          height:
            chartSettings.legendPosition === "bottom"
              ? "calc(100% - 100px)"
              : "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ChartAnnotations reportExtras={reportExtras} />
        <BarChart
          layout="vertical"
          data={data}
          width={dimensions.chartWidth}
          height={adjustedChartHeight}
          margin={dimensions.margin}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          {referenceLines &&
            referenceLines.map((refLine) => (
              <ReferenceLine
                key={refLine.label}
                x={refLine.y}
                stroke="#BBB"
                label={{
                  position: "top",
                  value: refLine.label,
                }}
              />
            ))}
          <ChartTooltipV2
            content={(props) => (
              <ChartTooltipV2Wrapper
                chartType="Horizontal Bar"
                seriesConfig={seriesConfig}
                showCount={chartSettings.showCount}
                {...props}
              />
            )}
          />
          {bars}
          {renderXAxis()}
          {renderYAxis()}
        </BarChart>
      </div>

      {chartSettings.includeLegend &&
        ["right", "bottom"].includes(
          chartSettings.legendPosition || "right",
        ) && (
          <LegendWrapperV2
            position={chartSettings.legendPosition || "right"}
            items={seriesConfig}
            onItemChange={handleLegendItemChange}
          />
        )}
    </div>
  );
};

export default React.memo(
  HorizontalBarChartComponentV2,
) as typeof HorizontalBarChartComponentV2;
